import React, { useId, useRef } from "react";
import { createPortal } from "react-dom";
import DialogHeader from "./DialogHeader";
import { handleModalKeys, useModalFocus } from "./modalFocus";

type ModalSize = "md" | "lg" | "xl" | "2xl" | "4xl" | "5xl" | "6xl";

interface ModalShellProps {
  title: string;
  subtitle?: string;
  /** The × button and Escape. */
  onClose: () => void;
  /** A click outside the dialog closes it too, where nothing typed can be lost (the split question, F42). */
  closeOnOutsideClick?: boolean;
  /** While something is being saved: Escape and the close button wait. */
  closeDisabled?: boolean;
  headerActions?: React.ReactNode;
  size?: ModalSize;
  zIndexClassName?: string;
  bodyClassName?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
};

/**
 * Every dialog's frame: rendered over the page in a portal, named by its title and
 * described by its subtitle, with focus moved in, kept in and given back on close (F-12),
 * the page behind it inert, and Escape to close unless `closeDisabled`. Nested dialogs
 * stack: only the topmost one handles Tab and Escape.
 */
export default function ModalShell({
  title,
  subtitle,
  onClose,
  closeOnOutsideClick = false,
  closeDisabled = false,
  headerActions,
  size = "2xl",
  zIndexClassName = "z-50",
  bodyClassName = "max-h-[calc(90vh-138px)] overflow-y-auto px-6 py-5",
  footer,
  children,
}: ModalShellProps) {
  const titleId = useId();
  const subtitleId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(overlayRef, dialogRef);
  return createPortal(
    <div
      ref={overlayRef}
      className={`fixed inset-0 ${zIndexClassName} app-modal-overlay flex items-center justify-center p-4`}
      onMouseDown={closeOnOutsideClick && !closeDisabled ? (event) => { if (event.target === event.currentTarget) onClose(); } : undefined}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle === undefined ? undefined : subtitleId}
        tabIndex={-1}
        onKeyDown={(event) => { handleModalKeys(event, closeDisabled, onClose); }}
        className={`app-modal-container relative flex max-h-[92vh] w-full ${SIZE_CLASS[size]} flex-col overflow-hidden outline-none`}
      >
        <DialogHeader
          title={title}
          titleId={titleId}
          subtitle={subtitle}
          subtitleId={subtitleId}
          onClose={onClose}
          closeDisabled={closeDisabled}
          actions={headerActions}
          className="px-6 py-4"
        />
        <div className={bodyClassName}>{children}</div>
        {footer ? <div className="app-modal-footer px-6 py-4">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
